import { useEffect, useState } from "react";
import { supabase } from "@/lib/supabase";
import {
  CONTACT_FIELDS,
  DETAIL_GROUPS,
  STATUS_OPTIONS,
  displayValue,
  formatDateTime,
  getStatusMeta,
  type MatchProfile,
} from "../adminConfig";
import MemberForm from "./MemberForm";

interface MemberDetailDrawerProps {
  profile: MatchProfile | null;
  canEdit: boolean;
  onClose: () => void;
  onUpdated: (profile: MatchProfile) => void;
}

function avatarClass(gender: string | null): string {
  if (gender === "男") return "bg-secondary-100 text-secondary-700";
  if (gender === "女") return "bg-accent-100 text-accent-700";
  return "bg-background-200 text-foreground-600";
}

const selectClass =
  "w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-100";

export default function MemberDetailDrawer({
  profile,
  canEdit,
  onClose,
  onUpdated,
}: MemberDetailDrawerProps) {
  const [status, setStatus] = useState("pending");
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"ok" | "err">("ok");
  const [copied, setCopied] = useState("");
  const [editing, setEditing] = useState(false);

  useEffect(() => {
    if (profile) {
      setStatus(profile.status || "pending");
      setMessage("");
      setCopied("");
      setEditing(false);
    }
  }, [profile]);

  if (!profile) return null;

  const meta = getStatusMeta(profile.status);

  const handleCopy = async (text: string, key: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      window.setTimeout(() => setCopied(""), 1600);
    } catch {
      setCopied("");
    }
  };

  const handleSaveStatus = async () => {
    setSaving(true);
    setMessage("");
    try {
      const { error } = await supabase
        .from("match_profiles")
        .update({ status })
        .eq("id", profile.id);
      if (error) throw error;
      onUpdated({ ...profile, status });
      setMessageType("ok");
      setMessage("状态已更新");
    } catch (err) {
      setMessageType("err");
      setMessage(err instanceof Error ? err.message : "更新失败，请稍后重试");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-foreground-950/40" onClick={onClose}></div>

      <div className="relative flex h-full w-full max-w-xl flex-col border-l border-background-200 bg-background-100">
        <div className="flex items-start justify-between gap-4 border-b border-background-200 bg-background-50 px-5 py-4">
          <div className="flex min-w-0 items-center gap-3">
            {profile.photo_url ? (
              <img
                src={profile.photo_url}
                alt={profile.name ? `${profile.name}的照片` : "会员照片"}
                className="h-11 w-11 shrink-0 rounded-full object-cover object-top"
              />
            ) : (
              <span
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-base font-medium ${avatarClass(
                  profile.gender,
                )}`}
              >
                {profile.name ? profile.name.slice(0, 1) : "?"}
              </span>
            )}
            <div className="min-w-0">
              <p className="truncate font-heading text-base font-semibold text-foreground-950">
                {profile.name || "未填写姓名"}
              </p>
              <p className="mt-0.5 truncate text-xs text-foreground-400">
                {profile.gender || "—"} · {profile.age ? `${profile.age} 岁` : "年龄未填"} · 提交于{" "}
                {formatDateTime(profile.created_at)}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1.5">
            {canEdit && !editing && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-300 bg-background-50 px-3 py-1.5 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-600"
              >
                <i className="ri-edit-line"></i>
                编辑资料
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-md text-foreground-500 transition-colors hover:bg-background-100 hover:text-foreground-900"
              aria-label="关闭"
            >
              <i className="ri-close-line text-xl"></i>
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto px-5 py-5">
          <div className="rounded-lg border border-background-200 bg-background-50 p-4">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium text-foreground-800">跟进状态</p>
                <p className="mt-0.5 text-xs text-foreground-400">
                  {!canEdit
                    ? "仅管理员可修改跟进状态"
                    : editing
                      ? "资料编辑中，可随时切换跟进状态"
                      : "标记该会员当前的处理进度"}
                </p>
              </div>
              <span
                className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ${meta.badge}`}
              >
                <span className={`h-1.5 w-1.5 rounded-full ${meta.dot}`}></span>
                {meta.label}
              </span>
            </div>
            {canEdit && (
              <>
                <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                  <select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    className={selectClass}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={handleSaveStatus}
                    disabled={saving}
                    className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-3-line"></i>}
                    保存
                  </button>
                </div>
                {message && (
                  <p
                    className={`mt-2 text-xs ${
                      messageType === "ok" ? "text-primary-600" : "text-accent-600"
                    }`}
                  >
                    {message}
                  </p>
                )}
              </>
            )}
          </div>

          {editing ? (
            <MemberForm
              key={profile.id}
              initial={profile}
              submitLabel="保存资料"
              cancelLabel="取消"
              onCancel={() => setEditing(false)}
              onSubmit={async (payload) => {
                const { data, error: updateError } = await supabase
                  .from("match_profiles")
                  .update(payload)
                  .eq("id", profile.id)
                  .select("*")
                  .maybeSingle();
                if (updateError) throw updateError;
                onUpdated(
                  (data as MatchProfile) || { ...profile, ...(payload as Partial<MatchProfile>) },
                );
              }}
            />
          ) : (
            <>
          <section className="rounded-lg border border-background-200 bg-background-50">
            <div className="flex items-center gap-2 border-b border-background-200 px-4 py-3">
              <span className="flex h-7 w-7 items-center justify-center rounded-md bg-primary-100 text-primary-600">
                <i className="ri-contacts-line text-sm"></i>
              </span>
              <h3 className="font-heading text-sm font-semibold text-foreground-900">联系方式</h3>
            </div>
            <div className="grid grid-cols-1 gap-3 px-4 py-4 sm:grid-cols-2">
              {CONTACT_FIELDS.map((field) => {
                const raw = profile[field.key];
                const text = raw ? String(raw) : "";
                return (
                  <div
                    key={field.key}
                    className="rounded-md border border-background-200 bg-background-50 px-3 py-2.5"
                  >
                    <p className="text-xs text-foreground-400">{field.label}</p>
                    <div className="mt-1 flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-medium text-foreground-900">
                        {text || "—"}
                      </span>
                      {text && (
                        <button
                          type="button"
                          onClick={() => handleCopy(text, field.key)}
                          className="shrink-0 cursor-pointer whitespace-nowrap rounded-md border border-background-300 px-2 py-0.5 text-xs text-foreground-600 transition-colors hover:border-primary-300 hover:text-primary-600"
                        >
                          {copied === field.key ? "已复制" : "复制"}
                        </button>
                      )}
                    </div>
                    {field.key === "phone" && !canEdit && text && (
                      <p className="mt-1 text-xs text-foreground-400">手机号为脱敏显示，完整号码仅管理员可见</p>
                    )}
                  </div>
                );
              })}
            </div>
          </section>

          {DETAIL_GROUPS.map((group) => (
            <section
              key={group.title}
              className="rounded-lg border border-background-200 bg-background-50"
            >
              <div className="flex items-center gap-2 border-b border-background-200 px-4 py-3">
                <span className="flex h-7 w-7 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
                  <i className={`${group.icon} text-sm`}></i>
                </span>
                <h3 className="font-heading text-sm font-semibold text-foreground-900">
                  {group.title}
                </h3>
              </div>
              <div className="grid grid-cols-1 gap-x-5 gap-y-4 px-4 py-4 sm:grid-cols-2">
                {group.fields.map((field) => (
                  <div key={field.key} className={field.full ? "sm:col-span-2" : ""}>
                    <p className="text-xs text-foreground-400">{field.label}</p>
                    <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-foreground-900">
                      {displayValue(profile[field.key], field.suffix)}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}